// Date utility functions for consistent formatting across the app

/**
 * Format date to dd-mm-yyyy format
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string in dd-mm-yyyy format
 */
export const formatDate = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  } catch (error) {
    return '';
  }
};

/**
 * Format date with time in 12-hour AM/PM format
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string in dd-mm-yyyy hh:mm AM/PM format
 */
export const formatDateTime = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 becomes 12
    
    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  } catch (error) {
    return '';
  }
};

/**
 * Format a time string (HH:mm) to 12-hour AM/PM format
 * @param {string} timeStr - Time string in HH:mm format (e.g., "14:30")
 * @returns {string} Formatted time in h:mm AM/PM format (e.g., "2:30 PM")
 */
export const formatTime = (timeStr) => {
  if (!timeStr) return '';
  
  try {
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    if (isNaN(hours)) return timeStr;
    
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    
    return `${hours}:${minutes} ${ampm}`;
  } catch (error) {
    return timeStr;
  }
};

/**
 * Format date for input fields (yyyy-MM-dd format required by HTML date inputs)
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string in yyyy-MM-dd format
 */
export const formatDateForInput = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    return '';
  }
};

/**
 * Parse dd-mm-yyyy format to Date object
 * @param {string} dateStr - Date string in dd-mm-yyyy format
 * @returns {Date|null} Date object or null if invalid
 */
export const parseDateFromDisplay = (dateStr) => {
  if (!dateStr) return null;
  
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);
  
  const date = new Date(year, month, day);
  return isNaN(date.getTime()) ? null : date;
};
